import fetchAdapter from "@vespaiach/axios-fetch-adapter";
import Services, {hostConfigFetched} from "../services/Services";
import {axiosInstance, getRequest, postRequest} from "../services/api";
import Subscriptions from "../Subscriptions";

export const getHostConfig = () => {
  if (!Services.host) {
    return;
  }

  return getRequest("/config.json", null, {
    noAuth: true,
    serverOptions: {serverURL: Services.host}
  });
};

export const fetchHostConfig = (host, oldHost) => {
  if (host && host === oldHost) {
    return;
  }

  const request = getHostConfig();

  if (!request) {
    return;
  }

  const {promise} = request;

  promise
    .then(({data}) => {
      Services.setServiceURLs(data, true);

      Subscriptions.publish(hostConfigFetched, data);
    })
    .catch(e => {
      console.error(e);
      // Empty means it'll parse the host
      Services.setServiceURLs();
      Subscriptions.publish(hostConfigFetched, {error: true});
    });

  return request;
};

export const whoami = () => {
  return getRequest("/iroh/profile/whoami");
};

export const getUserInfo = userId => {
  if (!userId) {
    return;
  }

  return getRequest(`/iroh/user-mgmt/users/${userId}`);
};

/* --- Used by the extension */
export const fetchAuthToken = (
  host = Services.host,
  clientId = Services.clientId,
  clientSecret = Services.clientSecret
) => {
  if (!host || !clientId || !clientSecret) {
    return;
  }

  const creds = btoa(`${clientId}:${clientSecret}`);

  const {promise, cancel} = postRequest(
    "/iroh/oauth2/token",
    "grant_type=client_credentials",
    {
      noAuth: true,
      requestOptions: {
        headers: {
          authorization: `Basic ${creds}`,
          contentType: "application/x-www-form-urlencoded"
        }
      },
      serverOptions: {serverURL: host}
    }
  );

  const _promise = promise
    .then(response => {
      const {data} = response,
        {access_token: credentials} = data;

      return Promise.resolve(credentials);
    })
    .catch(e => {
      console.error("Failed to fetch new token");
      throw e;
    });

  return {promise: _promise, cancel};
};

let _retrying = false,
  _refreshCall;

export const refreshToken = fetchFunction => {
  if (_retrying) {
    return _refreshCall;
  }

  _retrying = true;

  _refreshCall = fetchFunction();

  return _refreshCall;
};

export const configureAxiosRetry = (fetchFunction = fetchAuthToken) => {
  axiosInstance.interceptors.response.use(
    response => response,
    error => {
      const status = error.response ? error.response.status : null;

      // If we already retried once, just give up. If the credentials have been cleared, bail.
      if ((error.config && error.config.__isRetry) || !Services.credentials) {
        return Promise.reject(error);
      }

      if (status === 401) {
        // Fetch a new token
        const tokenRequest = refreshToken(fetchFunction);

        if (!tokenRequest) {
          return;
        }

        return tokenRequest.promise.then(credentials => {
          // Set the token for the retry
          error.config.headers.authorization = `Bearer ${credentials}`;

          error.config.baseURL = undefined;
          error.config.__isRetry = true;

          _retrying = false;
          _refreshCall = null;

          // Retry the request
          return axiosInstance.request(error.config).then(response => {
            // Set the token for future calls
            Services.credentials = credentials;

            return response;
          });
        });
      }

      return Promise.reject(error);
    }
  );

  Services.hasRetryInterceptor = true;
};

export const axiosFetchAdapter = fetchAdapter;
/* Used by the extension --- */
